import MultiTabComponent, { ITab } from "../../components/multi-tab-component/multi-tab-component";
import Page from "../../components/page/page";
import { noop } from "../../utils";
import Documents from "../alertcase/documents/documents";
import Notes from "../alertcase/notes/notes";

import "./aggregator-alertcase.scss";
import { useEffect, useState } from "react";

import History from "../alertcase/history/history";
import ControlledTable, { ITableRowData } from "../../components/controlled-table/controlled-table";
import { IColumn, ISortedBy } from "../../components/data-table/data-table";
import { useHttpGet, useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";
import { getData, postSingleNonTable } from "../../../shared/utils/api";
import AuthContext from "../../../shared/context/auth-context";
import { createUUID, validateLoadingStatusNew } from "../../../shared/utils/utils";
import Spinner from "../../../shared/components/spinner/spinner";
import SelectAlertCaseModal from "./select-alertcase-modal";

import { useContext } from "react";
import { useParams } from "react-router";


import { Button } from "react-bootstrap";
import { useNavigate } from "react-router";

const columns: IColumn[] = [
  {
    key: "alertcase_number",
    displayName: "AlertCase ID",
    dataType: {
      type: "text",
    }
  },
  {
    key: "title",
    displayName: "Title",
    dataType: {
      type: "text",
    }
  },
  {
    key: "creation_date",
    displayName: "Creation date",
    dataType: {
      type: "dateTime",
    }
  },
  {
    key: "process_type",
    displayName: "Process type",
    dataType: {
      type: "text",
    }
  },
  {
    key: "domain_type",
    displayName: "Domain type",
    dataType: {
      type: "text",
    }
  },
  {
    key: "consolidate_on_entity_type",
    displayName: "Consolidation",
    sortable: false,
    dataType: {
      type: "text",
    }
  },
  {
    key: "current_stage_name",
    displayName: "Stage",
    dataType: {
      type: "text",
    }
  },
  {
    key: "last_changed_by_name",
    displayName: "Last changed by",
    sortable: false,
    dataType: {
      type: "text",
    }
  },
  {
    key: "state",
    displayName: "Status",
    dataType: {
      type: "text",
    }
  }
];

const AggregatorAlertCase = () => {
  const [data, setData] = useState<ITableRowData>({id: "", name: "", alertcases: [], creation_date: undefined, created_by: ""});
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({column: "alertcase_id", direction: "desc"});
  const [showSelectAlertCaseModal, setShowSelectAlertCaseModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const authCtx = useContext(AuthContext);
  const params = useParams();
  const navigate = useNavigate();

  const { sendRequest: getAggregatorAlertCase, status: getAggregatorAlertCaseStatus, data: getAggregatorAlertCaseData, error: getAggregatorAlertCaseError } = useHttpGet(getData, false, authCtx.token, "default", "manager");

  const { sendRequest: postAggregatorAlertCase, status: postAggregatorAlertCaseStatus, data: postAggregatorAlertCaseData, error: postAggregatorAlertCaseError } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "default", "manager");
  const { sendRequest: getAlertCase, status: getAlertCaseStatus, data: getAlertCaseData, error: getAlertCaseError } = useHttpGet(getData, false, authCtx.token, "alertcaseRecord", "manager");
  
  useEffect(() => {
    if (params.id) {
      setData({id: params.id, name: "", alertcases: [], creation_date: undefined, created_by: ""});
    }
  }, []);

  useEffect(() => {
    if (params.id) {
      getAggregatorAlertCase(`aggregator_alertcase/${params.id}?order_by=${sortedByCol.column} ${sortedByCol.direction}`);
    }
  }, [sortedByCol]);

  useEffect(() => {
    if (getAggregatorAlertCaseStatus === "completed" && !getAggregatorAlertCaseError && getAggregatorAlertCaseData) {
      setData({...getAggregatorAlertCaseData});
      setSelectedIds(getAggregatorAlertCaseData.alertcases.map((c: ITableRowData) => c.id));
    }
  }, [getAggregatorAlertCaseStatus, getAggregatorAlertCaseData, getAggregatorAlertCaseError]);

  useEffect(() => {
    if (postAggregatorAlertCaseStatus === "completed" && !postAggregatorAlertCaseError && postAggregatorAlertCaseData) {
      setData({...postAggregatorAlertCaseData});
    }
  }, [postAggregatorAlertCaseStatus, postAggregatorAlertCaseData, postAggregatorAlertCaseError]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && !getAlertCaseError && getAlertCaseData) {
      navigate(`/manager/alertcases/${getAlertCaseData.id}`);
    }
  }, [getAlertCaseStatus, getAlertCaseData, getAlertCaseError]);

  const tabs: ITab[] = [{
    name: "Notes",
    key: "notes",
    content: <Notes alertcaseId={data.id} />
  }, {
    name: "Documents",
    key: "documents", 
    content: <Documents alertcaseId={data.id} />
  }, {
    name: "History",
    key: "history",
    content: <History alertcaseId={data.id} />,
  }];

  const handleOnSubmit = () => {
    const id = data.id || createUUID();
    const now = new Date().toISOString();
    
    postAggregatorAlertCase(`aggregator_alertcase/${id}`, {
      id,
      name: data.name,
      alertcase_ids: selectedIds,
      creation_date: data.creation_date || now,
      created_by: data.created_by || authCtx.userId
    });
  };

  const handleOnAlertCasesChanged = (selectedAlertCaseIds: string[]) => {
    const id = data.id || createUUID();
    const now = new Date().toISOString();
    
    setShowSelectAlertCaseModal(false);
    postAggregatorAlertCase(`aggregator_alertcase/${id}`, {
      id,
      name: data.name,
      alertcase_ids: selectedAlertCaseIds,
      creation_date: data.creation_date || now,
      created_by: data.created_by || authCtx.userId
    });
    setSelectedIds(selectedAlertCaseIds);
  };

  const handleOnDeleted = (deletedIds: string[]) => {
    const newData = {...data};
    newData.alertcases = newData.alertcases.filter((c: ITableRowData) => !deletedIds.includes(c.id));

    postAggregatorAlertCase(`aggregator_alertcase/${newData.id}`, {
      id: newData.id || createUUID(),
      name: newData.name,
      alertcase_ids: newData.alertcases.map((c: ITableRowData) => c.id),
      creation_date: newData.creation_date,
      created_by: newData.created_by
    });
    setSelectedIds(newData.alertcases.map((c: ITableRowData) => c.id));
  };

  const handleOnSorted = (sortedByCol: ISortedBy) => {
    setSortedByCol(sortedByCol);
  };

  const handleOnRowClick = (rowId: string) => {
    getAlertCase(`alertcase/${rowId}`);
  };

  return <Page title={{
    text: data.name,
    onTitleEdited: (title: string) => {
      setData({...data, name: title});
    },
    placeholder: "Aggregator AlertCase"
  }}>
    <div className="aggregatorAlertCase">
      {validateLoadingStatusNew([getAggregatorAlertCaseStatus, postAggregatorAlertCaseStatus]) && <Spinner />}
      <MultiTabComponent tabs={tabs} displayNextButton={false} />
      <SelectAlertCaseModal 
        show={showSelectAlertCaseModal} 
        selectedIds={selectedIds} 
        onClosed={() => setShowSelectAlertCaseModal(false)} 
        onSubmit={handleOnAlertCasesChanged} 
      />
      <div className="aggregatorAlertCase-alertcaseTable">
        <ControlledTable
          style="compact"
          columns={columns}
          data={data.alertcases}
          selectionMode="multi"
          onDeleted={handleOnDeleted}
          onNewClicked={() => setShowSelectAlertCaseModal(true)}
          onRowClick={handleOnRowClick}
          onSorted={handleOnSorted}
          onScroll={noop}
        />
      </div>
      <div className="aggregatorAlertCase-buttons">
        <Button
          onClick={handleOnSubmit}
          className="aggregatorAlertCase-saveButton"
          disabled={!data.name}
        >
          Save
        </Button>
      </div>
    </div>
  </Page>;
};    

export default AggregatorAlertCase;
